import React from "react";
import Box from "@mui/joy/Box";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import CircularProgress from "@mui/joy/CircularProgress";
import Alert from "@mui/joy/Alert";
import Chip from "@mui/joy/Chip";
import Accordion from "@mui/joy/Accordion";
import AccordionSummary from "@mui/joy/AccordionSummary";
import AccordionDetails from "@mui/joy/AccordionDetails";
import AccordionGroup from "@mui/joy/AccordionGroup";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export type AnalyticsCardProps = {
    title: React.ReactNode;
    subtitle: React.ReactNode;
    howToRead: React.ReactNode;
    watchFor?: React.ReactNode;
    snapshotLabel?: "period" | "now" | "lifetime";
    children: React.ReactNode;
};

/** Пункт «на что смотреть»: метрика → что значит рост / падение */
export type WatchSignal = {
    signal: React.ReactNode;
    /** Описание при росте метрики */
    ifGrows: string;
    /** Описание при падении метрики */
    ifFalls: string;
    /** false — рост плохой (отмены, риски и т.п.). По умолчанию true */
    growsIsGood?: boolean;
};

export function WatchSignals(props: { items: WatchSignal[] }) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {props.items.map((item, index) => {
                const growsGood = item.growsIsGood !== false;
                return (
                    <Box key={index}>
                        <Typography level="title-sm" sx={{ mb: 0.5 }}>
                            {item.signal}:
                        </Typography>
                        <Box
                            component="ul"
                            sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.5 }}
                        >
                            <Typography component="li" level="body-sm">
                                <Typography
                                    component="span"
                                    level="body-sm"
                                    color={growsGood ? "success" : "danger"}
                                >
                                    {growsGood ? "растёт (хорошо)" : "растёт (плохо)"}
                                </Typography>
                                {": "}
                                {item.ifGrows}
                            </Typography>
                            <Typography component="li" level="body-sm">
                                <Typography
                                    component="span"
                                    level="body-sm"
                                    color={growsGood ? "danger" : "success"}
                                >
                                    {growsGood ? "падает (плохо)" : "падает (хорошо)"}
                                </Typography>
                                {": "}
                                {item.ifFalls}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

export function AnalyticsCard(props: AnalyticsCardProps) {
    const { title, subtitle, howToRead, watchFor, snapshotLabel, children } = props;

    return (
        <Sheet
            variant="outlined"
            sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: "md",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                minWidth: 0,
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "space-between",
                    }}
                >
                    <Typography level="h4" component="h2">
                        {title}
                    </Typography>
                    {snapshotLabel === "now" && (
                        <Chip size="sm" variant="soft" color="warning">
                            Снимок сейчас
                        </Chip>
                    )}
                    {snapshotLabel === "period" && (
                        <Chip size="sm" variant="soft" color="neutral">
                            За выбранный период
                        </Chip>
                    )}
                    {snapshotLabel === "lifetime" && (
                        <Chip size="sm" variant="soft" color="primary">
                            Весь срок кампании
                        </Chip>
                    )}
                </Box>
                <Typography level="body-sm" color="neutral">
                    {subtitle}
                </Typography>
            </Box>

            <Alert color="neutral" variant="soft" size="sm" invertedColors startDecorator={<InfoOutlinedIcon />}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                    <Typography level="title-sm">Как читать</Typography>
                    <Typography level="body-sm" component="div">
                        {howToRead}
                    </Typography>

                    {watchFor && (
                        <AccordionGroup
                            size="sm"
                            variant="soft"
                            color="neutral"
                            sx={{ borderRadius: "sm", bgcolor: "background.level1" }}
                        >
                            <Accordion defaultExpanded={false}>
                                <AccordionSummary>
                                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                                        <InfoOutlinedIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                                        <Typography level="title-sm">На что смотреть</Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography level="body-sm" component="div">
                                        {watchFor}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        </AccordionGroup>
                    )}
                </Box>
            </Alert>

            {children}
        </Sheet>
    );
}

export function MetricHint(props: {
    label: React.ReactNode;
    value: React.ReactNode;
    hint?: React.ReactNode;
    color?: "primary" | "success" | "warning" | "danger" | "neutral";
}) {
    const { label, value, hint, color = "neutral" } = props;
    return (
        <Sheet
            variant="soft"
            color={color}
            sx={{
                p: 1.5,
                borderRadius: "md",
                minWidth: { xs: "100%", sm: 140 },
                flex: "1 1 140px",
            }}
        >
            <Typography level="body-xs" color="neutral" sx={{ mb: 0.5 }}>
                {label}
            </Typography>
            <Typography level="h3" sx={{ lineHeight: 1.2 }}>
                {value}
            </Typography>
            {hint && (
                <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                    {hint}
                </Typography>
            )}
        </Sheet>
    );
}

export function EmptyState(props: { hint?: string }) {
    return (
        <Box
            sx={{
                py: 4,
                px: 2,
                textAlign: "center",
                bgcolor: "background.level1",
                borderRadius: "md",
            }}
        >
            <Typography level="title-md">Нет данных за период</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
                {props.hint ?? "Попробуйте сменить период, игру или нажмите «Обновить»."}
            </Typography>
        </Box>
    );
}

export function BlockLoading() {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 3, justifyContent: "center" }}>
            <CircularProgress size="sm" />
            <Typography level="body-sm">Загрузка…</Typography>
        </Box>
    );
}

export function BlockError(props: { message?: string; onRetry?: () => void }) {
    return (
        <Alert
            color="danger"
            variant="soft"
            sx={{ alignItems: "flex-start" }}
            endDecorator={
                props.onRetry ? (
                    <Typography
                        level="body-sm"
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        onClick={props.onRetry}
                    >
                        Повторить
                    </Typography>
                ) : undefined
            }
        >
            {props.message ?? "Не удалось загрузить данные. Попробуйте обновить."}
        </Alert>
    );
}

export function ScrollTable(props: { children: React.ReactNode }) {
    return (
        <Box
            sx={{
                width: "100%",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
            }}
        >
            {props.children}
        </Box>
    );
}
